import React from 'react';
import { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatAgentValue } from '../lib/agentUnits';
import type { ChartSpec, ChartType, SeriesUnit } from '../types';

// Muted, dashboard-grade palette (matches the comparison hues in Topbar).
const PALETTE = [
  '#1f7a8c',
  '#c8963e',
  '#5e8c61',
  '#a35d6a',
  '#7a6c9e',
  '#3f7cac',
] as const;

const COMPARE_PALETTE = ['#1f7a8c', '#a35d6a', '#c8963e'] as const;

const COMPARE_TYPES = new Set<ChartType>([
  'compareBar',
  'compareLine',
  'compareArea',
]);

const AXIS_TICK = { fill: 'var(--ink-3)', fontSize: 13 };
const GRID_STROKE = 'var(--line)';

function formatValue(value: number, unit?: SeriesUnit): string {
  return formatAgentValue(value, unit, 0);
}

/**
 * Renders a server-hydrated ChartSpec with Recharts. Numbers come from
 * `spec.data` (hydrated from a real tool result on the backend); this component
 * only maps the declarative spec to a chart and never computes figures.
 */
export function ChartRenderer({
  spec,
  height = 240,
}: {
  spec: ChartSpec;
  height?: number;
}) {
  const data = spec.data ?? [];
  if (data.length === 0 || spec.series.length === 0) {
    return null;
  }

  const primaryUnit = spec.series[0]?.unit;
  const yTickFormatter = (v: number) => formatValue(v, primaryUnit);
  const tooltipFormatter = (value: number, name: string) => {
    const series = spec.series.find(s => s.label === name || s.key === name);
    return [formatValue(value, series?.unit), series?.label ?? name];
  };

  const comparing = COMPARE_TYPES.has(spec.type);
  const color = (i: number) =>
    comparing
      ? COMPARE_PALETTE[i % COMPARE_PALETTE.length]
      : PALETTE[i % PALETTE.length];

  return (
    <figure className="agent-chart">
      <figcaption className="agent-chart-title">{spec.title}</figcaption>
      <div className="agent-chart-canvas">
        {spec.type === 'heatmap' ? (
          <HeatmapGrid spec={spec} data={data} formatValue={formatValue} />
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </figure>
  );

  function renderChart(): React.ReactElement {
    if (spec.type === 'pie') {
      const valueKey = spec.series[0].key;
      return (
        <PieChart>
          <Tooltip
            formatter={(value: number) => formatValue(value, spec.series[0].unit)}
          />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          <Pie
            data={data}
            dataKey={valueKey}
            nameKey={spec.x.key}
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={48}
            paddingAngle={2}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={color(i)} />
            ))}
          </Pie>
        </PieChart>
      );
    }

    if (spec.type === 'area' || spec.type === 'compareArea') {
      return (
        <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={spec.x.key} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={yTickFormatter} width={48} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {spec.series.map((s, i) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={color(i)}
              fill={color(i)}
              fillOpacity={comparing && i > 0 ? 0.06 : 0.18}
              strokeWidth={2}
              strokeDasharray={comparing && i > 0 ? '6 4' : undefined}
            />
          ))}
        </AreaChart>
      );
    }

    if (
      spec.type === 'bar' ||
      spec.type === 'stackedBar' ||
      spec.type === 'compareBar' ||
      spec.type === 'horizontalBar'
    ) {
      const stackId = spec.type === 'stackedBar' ? 'stack' : undefined;
      const vertical = spec.type === 'horizontalBar';
      const categoryCells =
        comparing && spec.series.length === 1
          ? data.map((_, i) => <Cell key={i} fill={color(i)} />)
          : null;
      return (
        <BarChart
          data={data}
          layout={vertical ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 12, bottom: 4, left: vertical ? 16 : 4 }}
        >
          <CartesianGrid
            stroke={GRID_STROKE}
            strokeDasharray="3 3"
            vertical={!vertical}
            horizontal={vertical}
          />
          {vertical ? (
            <>
              <XAxis
                type="number"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                tickFormatter={yTickFormatter}
              />
              <YAxis
                type="category"
                dataKey={spec.x.key}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: GRID_STROKE }}
                width={88}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={spec.x.key}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: GRID_STROKE }}
              />
              <YAxis
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                tickFormatter={yTickFormatter}
                width={48}
              />
            </>
          )}
          <Tooltip formatter={tooltipFormatter} cursor={{ fill: 'var(--surface-2)' }} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {spec.series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId={stackId}
              fill={color(i)}
              fillOpacity={comparing && i > 0 ? 0.72 : 1}
              radius={stackId || vertical ? undefined : [3, 3, 0, 0]}
            >
              {i === 0 ? categoryCells : null}
            </Bar>
          ))}
        </BarChart>
      );
    }

    if (spec.type === 'scatter') {
      const yKey = spec.series[0].key;
      const numericX = typeof data[0]?.[spec.x.key] === 'number';
      return (
        <ScatterChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
          <XAxis
            dataKey={spec.x.key}
            name={spec.x.label}
            type={numericX ? 'number' : 'category'}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: GRID_STROKE }}
          />
          <YAxis
            dataKey={yKey}
            name={spec.series[0].label}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={yTickFormatter}
            width={48}
          />
          <Tooltip formatter={tooltipFormatter} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {spec.series.map((s, i) => (
            <Scatter key={s.key} name={s.label} dataKey={s.key} fill={color(i)} />
          ))}
        </ScatterChart>
      );
    }

    if (spec.type === 'combo') {
      const [bars, ...lines] = spec.series;
      const dual = spec.series.some(s => s.unit && s.unit !== primaryUnit);
      return (
        <ComposedChart data={data} margin={{ top: 8, right: dual ? 12 : 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={spec.x.key} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
          <YAxis
            yAxisId="left"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={yTickFormatter}
            width={48}
          />
          {dual ? (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              tickFormatter={v =>
                formatValue(v, spec.series.find(s => s.unit && s.unit !== primaryUnit)?.unit)
              }
              width={44}
            />
          ) : null}
          <Tooltip formatter={tooltipFormatter} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {bars ? (
            <Bar
              yAxisId="left"
              dataKey={bars.key}
              name={bars.label}
              fill={color(0)}
              radius={[3, 3, 0, 0]}
            />
          ) : null}
          {lines.map((s, i) => (
            <Line
              key={s.key}
              yAxisId={dual && s.unit && s.unit !== primaryUnit ? 'right' : 'left'}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={color(i + 1)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </ComposedChart>
      );
    }

    // default: line / compareLine
    return (
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={spec.x.key} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_STROKE }} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={yTickFormatter} width={48} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend wrapperStyle={{ fontSize: 13 }} />
        {spec.series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={color(i)}
            strokeWidth={2}
            strokeDasharray={comparing && i > 0 ? '6 4' : undefined}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    );
  }
}

const DOW_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function weekdayLabel(day: string): string {
  const idx = DOW_ORDER.indexOf(day);
  if (idx === -1) return day;
  const d = new Date(Date.UTC(2024, 0, 1 + idx));
  return d.toLocaleDateString(dateLocale(currentLocale()), { weekday: 'short' });
}

function HeatmapGrid({
  spec,
  data,
  formatValue,
}: {
  spec: ChartSpec;
  data: Array<Record<string, string | number>>;
  formatValue: (value: number, unit?: SeriesUnit) => string;
}) {
  const xKey = spec.x.key;
  const yKey = spec.y?.key ?? 'dayOfWeek';
  const vKey = spec.series[0].key;
  const unit = spec.series[0].unit;

  const hours = Array.from(
    new Set(data.map(row => String(row[xKey] ?? ''))),
  ).filter(Boolean).sort();
  const daysRaw = Array.from(
    new Set(data.map(row => String(row[yKey] ?? ''))),
  ).filter(Boolean);
  const days = [...daysRaw].sort((a, b) => {
    const ia = DOW_ORDER.indexOf(a);
    const ib = DOW_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const lookup = new Map<string, number>();
  let max = 0;
  for (const row of data) {
    const n = Number(row[vKey]);
    if (!Number.isFinite(n)) continue;
    lookup.set(`${row[yKey]}|${row[xKey]}`, n);
    if (n > max) max = n;
  }

  return (
    <div className="agent-heatmap" role="img" aria-label={spec.title}>
      <div
        className="agent-heatmap-grid"
        style={{
          gridTemplateColumns: `72px repeat(${hours.length}, minmax(18px, 1fr))`,
        }}
      >
        <div className="agent-heatmap-corner" />
        {hours.map(h => (
          <div key={h} className="agent-heatmap-x">
            {h.replace(/:00$/, '')}
          </div>
        ))}
        {days.map(day => (
          <React.Fragment key={day}>
            <div className="agent-heatmap-y">{weekdayLabel(day)}</div>
            {hours.map(hour => {
              const value = lookup.get(`${day}|${hour}`) ?? 0;
              const t = max > 0 ? value / max : 0;
              return (
                <div
                  key={`${day}-${hour}`}
                  className="agent-heatmap-cell"
                  title={`${weekdayLabel(day)} ${hour}: ${formatValue(value, unit)}`}
                  style={{
                    background: `color-mix(in srgb, ${PALETTE[5]} ${Math.round(t * 86)}%, var(--surface-2))`,
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
